import { TICKETS_ORIGIN } from 'constants/api';

/**
 * Compute the WebSocket base URL from the ticketing origin.
 *
 * @return {string}
 */
function getWsBase() {
    const base = TICKETS_ORIGIN.replace(/\/$/, '');
    return base.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
}

/**
 * Client that communicates with the SharedWorker via MessagePort.
 * Maintains per-ticket callback registrations to route incoming
 * events to the correct saga eventChannel.
 *
 * @class SharedCableWorkerClient
 */
class SharedCableWorkerClient {
    constructor(worker) {
        this.port = worker.port;
        // { ticketId → { received, connected, disconnected, rejected } }
        this.callbacks = {};

        this.port.onmessage = event => {
            this._handleMessage(event.data);
        };
        this.port.start();

        // Tell the worker the WebSocket base URL
        this.port.postMessage({ type: 'init', wsBase: getWsBase() });
    }

    /**
     * Subscribe to presence updates for a ticket.
     *
     * @param {string} ticketId
     * @param {string} token
     * @param {Object} cbs  Callback map: { received, connected, disconnected, rejected }
     */
    subscribe(ticketId, token, cbs) {
        this.callbacks[ticketId] = cbs;
        this.port.postMessage({ type: 'subscribe', ticketId, token });
    }

    /**
     * Unsubscribe from a ticket.
     *
     * @param {string} ticketId
     */
    unsubscribe(ticketId) {
        delete this.callbacks[ticketId];
        this.port.postMessage({ type: 'unsubscribe', ticketId });
    }

    /**
     * Notify the worker of a token change.
     *
     * @param {string} token
     */
    updateToken(token) {
        this.port.postMessage({ type: 'updateToken', token });
    }

    /**
     * Full disconnect (logout).
     */
    disconnect() {
        this.callbacks = {};
        this.port.postMessage({ type: 'disconnect' });
    }

    /**
     * Route an incoming worker message to the registered callback.
     *
     * @private
     * @param {Object} data  The message from the worker
     */
    _handleMessage(data) {
        const { ticketId } = data;
        const cbs = this.callbacks[ticketId];
        if (!cbs) return;

        switch (data.type) {
            case 'viewers_updated':
            case 'ticket_edited':
                if (cbs.received) cbs.received(data);
                break;
            case 'connected':
                if (cbs.connected) cbs.connected();
                break;
            case 'disconnected':
                if (cbs.disconnected) cbs.disconnected();
                break;
            case 'rejected':
                if (cbs.rejected) cbs.rejected();
                break;
            default:
                break;
        }
    }
}

export default SharedCableWorkerClient;
